import { useState, useEffect, useCallback, useRef } from "react";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { DialogModal } from "../../components/ui/dialogModal";
import {
    Database, CheckCircle, AlertCircle, Copy, RefreshCw,
    Loader2, Trash2, Link2, Clock, Building2,
} from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import {
    ERP_CONNECTION_GENERATE,
    ERP_CONNECTION_STATUS,
    ERP_CONNECTION_REVOKE,
} from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";

// ── Helper: format date ───────────────────────────────────────────────────────

const formatDate = (iso) => {
    if (!iso) return "Never";
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
    });
};

// ── Main component ────────────────────────────────────────────────────────────

export default function ERPIntegration() {
    const { fetch, create } = useApiMethods();

    const [loading, setLoading] = useState(true);
    const [connection, setConnection] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [revoking, setRevoking] = useState(false);
    const [generatedToken, setGeneratedToken] = useState(null);
    const [revokeOpen, setRevokeOpen] = useState(false);

    const tokenRef = useRef(null);

    // ── Load connection status ────────────────────────────────────────────────

    const loadStatus = useCallback(async () => {
        setLoading(true);
        const res = await fetch(ERP_CONNECTION_STATUS);
        if (res?.status === "success") {
            setConnection(res.data);
        }
        setLoading(false);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { loadStatus(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Generate token ────────────────────────────────────────────────────────

    const handleGenerate = async () => {
        setGenerating(true);
        const res = await create(ERP_CONNECTION_GENERATE, {});
        setGenerating(false);
        if (res?.status === "success") {
            setGeneratedToken(res.data.token);
            ToastMsg("success", "Connection token generated successfully");
            loadStatus();
        } else {
            ToastMsg("error", res?.message || "Failed to generate connection token");
        }
    };

    // ── Copy token ────────────────────────────────────────────────────────────

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(generatedToken);
            ToastMsg("success", "Token copied to clipboard");
        } catch {
            // Fallback for older browsers
            if (tokenRef.current) {
                tokenRef.current.select();
                document.execCommand("copy");
                ToastMsg("success", "Token copied to clipboard");
            }
        }
    };

    // ── Revoke connection ─────────────────────────────────────────────────────

    const handleRevoke = async () => {
        setRevoking(true);
        const res = await create(ERP_CONNECTION_REVOKE, {});
        setRevoking(false);
        setRevokeOpen(false);
        if (res?.status === "success") {
            ToastMsg("success", "ERP connection revoked");
            setConnection(null);
            setGeneratedToken(null);
        } else {
            ToastMsg("error", res?.message || "Failed to revoke connection");
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div className="p-6 space-y-6">
            <ContainerCard>
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                            ERP Integration
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                            Connect your CRM to an external ERP platform
                        </p>
                    </div>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                    </div>
                ) : connection ? (
                    /* ── Connected state ─────────────────────────────── */
                    <div className="space-y-4">
                        <div className="p-5 rounded-xl border-2 border-blue-200 bg-blue-50 dark:bg-blue-900/10 dark:border-blue-800">
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/30 dark:to-blue-800/30 rounded-lg flex items-center justify-center">
                                        <Database className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                                    </div>
                                    <div>
                                        <span className="text-xs font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400">
                                            ERP Connection
                                        </span>
                                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 mt-0.5">
                                            {connection.org_name}
                                        </p>
                                    </div>
                                </div>
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                    <CheckCircle className="h-3 w-3" />Active
                                </span>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                    <Building2 className="h-3.5 w-3.5 flex-shrink-0" />
                                    <span className="text-xs">Tenant: {connection.tenant_db}</span>
                                </div>
                                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                    <Link2 className="h-3.5 w-3.5 flex-shrink-0" />
                                    <span className="text-xs font-mono">API Key: {connection.api_key}</span>
                                </div>
                                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                    <Clock className="h-3.5 w-3.5 flex-shrink-0" />
                                    <span className="text-xs">Created: {formatDate(connection.created_at)}</span>
                                </div>
                                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                    <Clock className="h-3.5 w-3.5 flex-shrink-0" />
                                    <span className="text-xs">Last Connected: {formatDate(connection.last_connected_at)}</span>
                                </div>
                            </div>

                            <div className="flex items-center gap-3 pt-4 mt-4 border-t border-blue-200 dark:border-blue-800">
                                <Button
                                    onClick={handleGenerate}
                                    disabled={generating}
                                    className="flex items-center gap-2 text-sm"
                                    variant="outline"
                                >
                                    {generating ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <RefreshCw className="h-4 w-4" />
                                    )}
                                    Regenerate Token
                                </Button>
                                <Button
                                    onClick={() => setRevokeOpen(true)}
                                    variant="outline"
                                    className="flex items-center gap-2 text-sm text-red-600 border-red-300 hover:bg-red-50 dark:text-red-400 dark:border-red-700 dark:hover:bg-red-900/20"
                                >
                                    <Trash2 className="h-4 w-4" />
                                    Revoke Connection
                                </Button>
                            </div>
                        </div>

                        {/* Show token if just generated */}
                        {generatedToken && (
                            <div className="p-4 rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-900/10 dark:border-amber-800">
                                <div className="flex items-center gap-2 mb-2">
                                    <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                                    <span className="text-sm font-medium text-amber-800 dark:text-amber-300">
                                        Connection Token Generated
                                    </span>
                                </div>
                                <p className="text-xs text-amber-700 dark:text-amber-400 mb-3">
                                    Copy this token and paste it in your ERP platform under Settings &rarr; Connect CRM.
                                    This token will not be shown again.
                                </p>
                                <div className="flex gap-2">
                                    <textarea
                                        ref={tokenRef}
                                        readOnly
                                        value={generatedToken}
                                        rows={3}
                                        className="flex-1 text-xs font-mono p-2 rounded-lg border border-amber-300 bg-white dark:bg-gray-900 dark:border-amber-700 text-gray-800 dark:text-gray-200 resize-none"
                                    />
                                    <Button
                                        onClick={handleCopy}
                                        variant="outline"
                                        className="flex items-center gap-1 text-sm self-start"
                                    >
                                        <Copy className="h-4 w-4" />
                                        Copy
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    /* ── No connection state ─────────────────────────── */
                    <div className="text-center py-12">
                        <div className="w-14 h-14 bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/20 dark:to-blue-800/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                            <Database className="h-6 w-6 text-blue-500" />
                        </div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            No ERP connection configured
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 mb-4">
                            Generate a connection token to link your CRM data with an external ERP platform
                        </p>
                        <Button
                            onClick={handleGenerate}
                            disabled={generating}
                            className="inline-flex items-center gap-2 text-sm"
                        >
                            {generating ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Link2 className="h-4 w-4" />
                            )}
                            {generating ? "Generating..." : "Generate Connection Token"}
                        </Button>

                        {/* Show token if just generated */}
                        {generatedToken && (
                            <div className="mt-6 p-4 rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-900/10 dark:border-amber-800 text-left max-w-lg mx-auto">
                                <div className="flex items-center gap-2 mb-2">
                                    <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                        Connection Token Generated
                                    </span>
                                </div>
                                <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                                    Copy this token and paste it in your ERP platform under Settings &rarr; Connect CRM.
                                    This token will not be shown again.
                                </p>
                                <div className="flex gap-2">
                                    <textarea
                                        ref={tokenRef}
                                        readOnly
                                        value={generatedToken}
                                        rows={3}
                                        className="flex-1 text-xs font-mono p-2 rounded-lg border border-gray-300 bg-white dark:bg-gray-900 dark:border-gray-700 text-gray-800 dark:text-gray-200 resize-none"
                                    />
                                    <Button
                                        onClick={handleCopy}
                                        variant="outline"
                                        className="flex items-center gap-1 text-sm self-start"
                                    >
                                        <Copy className="h-4 w-4" />
                                        Copy
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </ContainerCard>

            {/* Revoke Confirmation Modal */}
            <DialogModal
                open={revokeOpen}
                onClose={() => setRevokeOpen(false)}
                title="Revoke ERP Connection"
                width="420px"
                showActions={false}
            >
                <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                        <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                        <p className="text-sm text-red-700 dark:text-red-400">
                            This will immediately disconnect the ERP from your CRM. The ERP will no longer be able to pull data.
                        </p>
                    </div>
                    <div className="flex gap-2 pt-1">
                        <Button
                            variant="outline"
                            onClick={() => setRevokeOpen(false)}
                            className="flex-1 text-sm"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleRevoke}
                            disabled={revoking}
                            className="flex-1 text-sm flex items-center gap-2 justify-center bg-red-600 hover:bg-red-700 text-white"
                        >
                            {revoking && <Loader2 className="h-4 w-4 animate-spin" />}
                            {revoking ? "Revoking..." : "Revoke Connection"}
                        </Button>
                    </div>
                </div>
            </DialogModal>
        </div>
    );
}
